import React, { useState } from "react";
import { Form, Input, Button, Typography, Checkbox } from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  UserOutlined,
  LockOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTheme } from "../../store/context/theme-context";
import { useNotification } from "../../store/context/notification-context";
import { useApiRequests } from "../../utils/api/https/executor";
import { useTenant } from "../../store/context/tenant-context";
import {
  setAdminLogin,
  updateUserData,
} from "../../store/redux/actions/userActions";
import { usePermissions } from "../../store/context/permission-context";
import TwoFactorMethodSelector from "../components/TwoFactorMethodSelector";
import TwoFactorSetup from "../components/TwoFactorSetup";
import { OtpVerificationMethodEnum } from "../../shared/constants/constants";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";
import { takePostLoginDestination } from "../../utils/helper/entityProcessorRedirect";

const { Title, Text, Link } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();
  const { loginUser, loginAdmin } = useApiRequests();
  const { updatePermissions } = usePermissions();

  const isAdmin = location.pathname.includes("/admin");
  const isDark = theme === "dark";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [selectedVerificationMethod, setSelectedVerificationMethod] =
    useState(null);
  const [setupData, setSetupData] = useState({
    qrCodeUrl: "",
    secretKey: "",
  });

  const isDisabled = formData.email === "" || formData.password === "";

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInitialSubmit = async () => {
    // Prevent double submission
    if (isLoading) {
      console.log(
        "⚠️ Login already in progress, ignoring duplicate submission",
      );
      return;
    }

    // For customers, use default EMAIL method directly
    // For admins, show the 2FA method selector modal
    if (isAdmin) {
      setShowMethodSelector(true);
    } else {
      // Customers automatically use EMAIL verification
      handleMethodSelected(OtpVerificationMethodEnum.EMAIL.value);
    }
  };

  const handleMethodSelected = async (verificationMethodType) => {
    try {
      setIsLoading(true);
      setShowMethodSelector(false);

      const dataToPost = {
        userId: formData.email,
        password: formData.password,
        otpVerificationRequestId: "",
        verificationMethodType,
      };

      console.log(
        "Login request:",
        { ...dataToPost, password: "***" },
        "Method:",
        verificationMethodType,
      );

      const response = isAdmin
        ? await loginAdmin(dataToPost)
        : await loginUser(dataToPost);
      const result = response?.outData;

      console.log("Login response:", result);

      if (result.isSuccessful) {
        if (result.statusCode === "00") {
          // Direct login success (no 2FA required)
          if (isAdmin) {
            dispatch(setAdminLogin(result.data));
          } else {
            dispatch(updateUserData(result.data));
          }
          updatePermissions();
          navigate(
            takePostLoginDestination(
              isAdmin ? "/admin-dashboard" : "/dashboard",
            ),
          );
        } else if (result.data?.requiresSetup) {
          // First-time authenticator app setup
          setSetupData({
            qrCodeUrl: result.data.qrCodeUrl,
            secretKey: result.data.secretKey,
          });
          setSelectedVerificationMethod(verificationMethodType);
          setShowTwoFactorSetup(true);
        } else {
          // Navigate to OTP verification
          const loginContext = JSON.stringify({
            userId: dataToPost.userId,
            emailAddress: formData.email,
            isAdmin,
            ...result.data,
            verificationMethodType, // ✅ Put AFTER spread to ensure it's not overwritten
            requestId:
              result.data?.otpVerificationRequestId || result.data?.requestId, // ✅ Ensure requestId is set
          });

          navigate("/authentication/otp", {
            state: { loginContext },
          });
        }
      } else {
        if (result.statusCode === "720") {
          triggerNotification({
            type: "info",
            message: isAdmin
              ? "Login from Customer Portal"
              : "Login from Admin Portal",
            description:
              result?.message || "Please use the correct portal to login.",
          });
          navigate(
            isAdmin ? "/authentication/login" : "/authentication/admin/login",
          );
        } else {
          triggerNotification({
            type: "error",
            message: "Login Failed",
            description:
              result?.message || "Failed to login. Please try again later.",
          });
        }
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred during login. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupComplete = async (verificationCode) => {
    try {
      setIsLoading(true);

      // Verify the setup code by calling login with setupVerificationCode
      const dataToPost = {
        userId: formData.email,
        password: formData.password,
        otpVerificationRequestId: "",
        verificationMethodType: selectedVerificationMethod,
        setupVerificationCode: verificationCode,
      };

      const response = isAdmin
        ? await loginAdmin(dataToPost)
        : await loginUser(dataToPost);
      const result = response?.outData;

      if (result?.isSuccessful) {
        setShowTwoFactorSetup(false);

        // Check if backend returned full login response (setup complete with auto-login)
        if (result.data?.accessToken) {
          // Complete login - backend has already authenticated the user
          if (isAdmin) {
            await dispatch(setAdminLogin(result.data));
          } else {
            await dispatch(updateUserData(result.data));
          }

          updatePermissions();

          triggerNotification({
            type: "success",
            message: "Success",
            description: "Authenticator app setup complete! Login successful.",
          });

          navigate(
            takePostLoginDestination(
              isAdmin ? "/admin-dashboard" : "/dashboard",
            ),
          );
        } else {
          // Setup successful but needs OTP verification
          const loginContext = JSON.stringify({
            userId: formData.email,
            emailAddress: formData.email,
            isAdmin,
            ...result.data,
            verificationMethodType: selectedVerificationMethod, // ✅ Put AFTER spread to ensure it's not overwritten
            requestId:
              result.data?.otpVerificationRequestId || result.data?.requestId, // ✅ Ensure requestId is set
          });

          navigate("/authentication/otp", {
            state: { loginContext },
          });
        }
      } else {
        triggerNotification({
          type: "error",
          message: "Verification Failed",
          description:
            result?.message || "Invalid verification code. Please try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred during verification. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: isDark
          ? `linear-gradient(135deg, #0f0c29 0%, ${tenant.theme.colorPrimary}15 50%, #24243e 100%)`
          : `linear-gradient(135deg, ${tenant.theme.colorPrimary}20 0%, ${tenant.theme.colorPrimary}40 50%, ${tenant.theme.colorPrimary}30 100%)`,
        padding: "20px",
      }}
    >
      {/* Professional Animated Background */}
      <style>
        {`
          @keyframes floatAuth {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -30px) rotate(120deg); }
            66% { transform: translate(-20px, 20px) rotate(240deg); }
          }
          
          @keyframes pulseAuth {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.15); }
          }
          
          @keyframes driftAuth {
            0% { transform: translate(0, 0); }
            25% { transform: translate(20px, -20px); }
            50% { transform: translate(40px, 10px); }
            75% { transform: translate(20px, 30px); }
            100% { transform: translate(0, 0); }
          }
          
          @keyframes gradientWave {
            0% { transform: translateX(-50%) translateY(-50%); }
            50% { transform: translateX(-40%) translateY(-60%); }
            100% { transform: translateX(-50%) translateY(-50%); }
          }
          
          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }

          .auth-floating-shape {
            position: absolute;
            border-radius: 50%;
            filter: blur(60px);
            opacity: 0.4;
          }
          
          .auth-shape-1 {
            width: 400px;
            height: 400px;
            background: ${isDark ? "rgba(255, 255, 255, 0.08)" : `${tenant.theme.colorPrimary}30`};
            top: -100px;
            right: -100px;
            animation: floatAuth 25s ease-in-out infinite;
          }
          
          .auth-shape-2 {
            width: 300px;
            height: 300px;
            background: ${isDark ? "rgba(255, 255, 255, 0.05)" : `${tenant.theme.colorPrimary}25`};
            bottom: -80px;
            left: -80px;
            animation: floatAuth 20s ease-in-out infinite reverse;
          }
          
          .auth-shape-3 {
            width: 250px;
            height: 250px;
            background: ${isDark ? "rgba(255, 255, 255, 0.06)" : `${tenant.theme.colorPrimary}20`};
            top: 50%;
            left: 20%;
            animation: pulseAuth 15s ease-in-out infinite;
          }
          
          .auth-particle {
            position: absolute;
            width: 3px;
            height: 3px;
            background: ${isDark ? "rgba(255, 255, 255, 0.4)" : tenant.theme.colorPrimary};
            border-radius: 50%;
            box-shadow: 0 0 8px ${isDark ? "rgba(255, 255, 255, 0.3)" : `${tenant.theme.colorPrimary}80`};
          }

          .login-card-wrapper {
            position: relative;
            z-index: 1;
          }

          .login-card-wrapper::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(
              45deg,
              ${tenant.theme.colorPrimary}40,
              transparent,
              ${tenant.theme.colorPrimary}40
            );
            border-radius: 22px;
            opacity: 0;
            transition: opacity 0.3s ease;
            z-index: -1;
            animation: shimmer 3s infinite;
            background-size: 1000px 100%;
          }

          .login-card-wrapper:hover::before {
            opacity: 0.5;
          }
        `}
      </style>

      {/* Animated Gradient Overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, ${tenant.theme.colorPrimary}15 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${tenant.theme.colorPrimary}10 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${tenant.theme.colorPrimary}12 0%, transparent 50%)
          `,
          animation: "gradientWave 20s ease infinite",
        }}
      />

      {/* Floating Animated Shapes */}
      <div className="auth-floating-shape auth-shape-1" />
      <div className="auth-floating-shape auth-shape-2" />
      <div className="auth-floating-shape auth-shape-3" />

      {/* Floating Particles */}
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className="auth-particle"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `driftAuth ${12 + Math.random() * 8}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
            opacity: 0.2 + Math.random() * 0.3,
          }}
        />
      ))}

      {/* Language Selector - Top Right */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10,
          display: "flex",
          gap: "12px",
        }}
      >
        {/* Development Tenant Switcher - Only show on localhost */}
        {process.env.NODE_ENV === "development" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1") && <TenantSwitcher />}
        <LanguageSelector />
      </div>

      {/* Back to Landing - Top Left */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/landing")}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          color: isDark ? "#ffffff" : "#666666",
          zIndex: 10,
          fontWeight: 500,
        }}
      >
        Back to Home
      </Button>

      {/* Login Card */}
      <div
        className="login-card-wrapper"
        style={{ width: "100%", maxWidth: "480px" }}
      >
        <div
          style={{
            backgroundColor: isDark
              ? "rgba(31, 31, 31, 0.95)"
              : "rgba(255, 255, 255, 0.98)",
            borderRadius: "20px",
            padding: "56px 48px",
            boxShadow: isDark
              ? "0 30px 80px rgba(0, 0, 0, 0.5)"
              : "0 30px 80px rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(10px)",
            position: "relative",
          }}
        >
          {/* Logo and Title */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <img
              src={isDark ? tenant.darkLogo : tenant.logo}
              alt={tenant.name}
              style={{
                maxWidth: "100px",
                height: "auto",
                marginBottom: "20px",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                filter: isDark ? "brightness(1.1)" : "none",
              }}
            />
            <Title
              level={2}
              style={{
                margin: "0 0 8px 0",
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "28px",
                fontWeight: 600,
              }}
            >
              {isAdmin ? "Admin Portal" : "Welcome Back"}
            </Title>
            <Text
              style={{
                color: isDark ? "#a0a0a0" : "#666666",
                fontSize: "15px",
              }}
            >
              {isAdmin
                ? "Access your administrative dashboard"
                : `Sign in to your ${tenant.name} account`}
            </Text>
          </div>

          {/* Login Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleInitialSubmit}
            requiredMark={false}
          >
            <Form.Item
              label={
                <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                  Email or Username
                </span>
              }
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please enter your email or username",
                },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
                placeholder="Enter your email or username"
                size="large"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                  Password
                </span>
              }
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#8c8c8c" }} />}
                placeholder="Enter your password"
                size="large"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            {/* Remember Me and Forgot Password */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <Checkbox
                checked={formData.rememberMe}
                onChange={(e) =>
                  handleInputChange("rememberMe", e.target.checked)
                }
                style={{ color: isDark ? "#ffffff" : "#000000" }}
              >
                Remember me
              </Checkbox>
              <Link
                onClick={() => navigate("/authentication/forgot-password-new")}
                style={{ color: tenant.theme.colorPrimary }}
              >
                Forgot password?
              </Link>
            </div>

            {/* Login Button */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={isLoading}
                disabled={isDisabled || isLoading}
                style={{
                  backgroundColor: tenant.theme.colorPrimary,
                  borderColor: tenant.theme.colorPrimary,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>

          {/* Sign Up Link - Only for Customer Login */}
          {!isAdmin && (
            <div style={{ textAlign: "center", marginTop: "24px" }}>
              <Text style={{ color: isDark ? "#8c8c8c" : "#595959" }}>
                Don't have an account?{" "}
                <Link
                  onClick={() => navigate("/authentication?mode=signup")}
                  style={{
                    color: tenant.theme.colorPrimary,
                    fontWeight: "500",
                  }}
                >
                  Sign up
                </Link>
              </Text>
            </div>
          )}

          {/* Switch Portal Link */}
          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <Link
              onClick={() =>
                navigate(
                  isAdmin
                    ? "/authentication/login"
                    : "/authentication/admin/login",
                )
              }
              style={{
                color: isDark ? "#8c8c8c" : "#595959",
                fontSize: "14px",
              }}
            >
              Login as {isAdmin ? "Customer" : "Admin"} →
            </Link>
          </div>

          {/* Powered by BankLingo */}
          <div
            style={{
              textAlign: "center",
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: `1px solid ${isDark ? "#303030" : "#f0f0f0"}`,
            }}
          >
            <Text
              style={{
                color: isDark ? "#8c8c8c" : "#8c8c8c",
                fontSize: "12px",
              }}
            >
              {tenant.name} • Powered by{" "}
              <span style={{ fontWeight: "600" }}>BankLingo</span>
            </Text>
          </div>
        </div>
      </div>

      {/* 2FA Method Selector Modal */}
      <TwoFactorMethodSelector
        visible={showMethodSelector}
        onSelect={handleMethodSelected}
        onCancel={() => setShowMethodSelector(false)}
        loading={isLoading}
      />

      {/* 2FA Setup Modal (for first-time authenticator setup) */}
      <TwoFactorSetup
        visible={showTwoFactorSetup}
        onCancel={() => setShowTwoFactorSetup(false)}
        qrCodeUrl={setupData.qrCodeUrl}
        secretKey={setupData.secretKey}
        onVerify={handleSetupComplete}
        loading={isLoading}
      />
    </div>
  );
};

export default Login;
